import type { Home } from '@lmc/ts-common'
import type { FollowUpActionEntry } from '@lmc/validations'
import { Form as AntdForm } from 'antd'
import { getNominative } from 'common/utils'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import IncidentCaseV2StepForm from '../IncidentCaseV2StepForm'
import type {
  IncidentCaseV2StepProps,
  IncidentCaseV2StepValues
} from '../incidentCaseV2Steps'
import FollowUpActionList from './followUpActions/FollowUpActionList'
import {
  type FollowUpActionValue,
  toActionRequest
} from './followUpActions/followUpActionValues'

const testId = 'incident-case-v2-follow-up-actions'

type FollowUpActionsValues = {
  actions: Array<FollowUpActionValue>
}

/** The saved follow-up as the read returns it; absent until the step is saved. */
type SavedFollowUp = {
  followUpActions?: {
    actions?: Array<FollowUpActionEntry>
    notificationRequired?: boolean
    safeguardingActionsRequired?: boolean
  } | null
}

/**
 * Step 6: the actions that follow the incident, each with an owner, a due date
 * and a status, added from scratch or from a template.
 *
 * `followUpActions` is saved whole. The two flags beside the actions are not in
 * this step's design, so they go back as they were saved.
 */
const FollowUpActionsStep = ({
  incidentReport,
  handleRef,
  onDirtyChange
}: IncidentCaseV2StepProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const saved = (incidentReport.case as SavedFollowUp | null)?.followUpActions
  const notificationRequired = saved?.notificationRequired ?? false
  const safeguardingActionsRequired =
    saved?.safeguardingActionsRequired ?? false

  const initialValues = useMemo<FollowUpActionsValues>(
    () => ({
      actions: (saved?.actions ?? []).map(toActionRequest)
    }),
    [saved]
  )

  const toStepValues = (
    values: FollowUpActionsValues
  ): IncidentCaseV2StepValues => ({
    followUpActions: {
      actions: values.actions.map(toActionRequest),
      notificationRequired,
      safeguardingActionsRequired
    }
  })

  const templateValues = {
    carePlan: getNominative(
      home?.preferredCarePlansNominative,
      t(incidentReportsConstants.CASE_V2_ACTIONS_CARE_PLAN)
    ),
    name: incidentReport.serviceUser.name ?? ''
  }

  return (
    <IncidentCaseV2StepForm
      handleRef={handleRef}
      onDirtyChange={onDirtyChange}
      initialValues={initialValues}
      toStepValues={toStepValues}
      testId={testId}
    >
      <AntdForm.Item name="actions" noStyle>
        <FollowUpActionList templateValues={templateValues} testId={testId} />
      </AntdForm.Item>
    </IncidentCaseV2StepForm>
  )
}

export default FollowUpActionsStep
